import { RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import FullBleedScroller from "components/BeyondToolkit/FullBleedScroller";
import FullWidthButton from "components/FullWidthButton";
import { IdeasCloseIcon } from "components/IdeasIcons";
import RoundedIconButton from "components/RoundedIconButton";
import { SyntheticEvent, useCallback, useEffect, useMemo } from "react";
import { logNpsSurveyEvent } from "src/utils/logEvent";
import CommentSection from "./CommentSection";
import SurveyHeader from "./SurveyHeader";
import SurveyQuestion from "./SurveyQuestion";

type CommentPageProps = {
  comment: string;
  updateComment: (event: SyntheticEvent<HTMLTextAreaElement>) => void;
  score: number | null;
  submitSurvey: (copyType: string) => void;
  closeSurvey: VoidFunction;
  innerHeight: string | number;
};

export default function CommentPage({
  comment,
  updateComment,
  score,
  submitSurvey,
  closeSurvey,
  innerHeight,
}: CommentPageProps) {
  const copyType = useMemo(() => {
    if (score === null) {
      throw new Error("unexpected score");
    }

    switch (true) {
      case score <= 6:
        return "detractor";
      case score >= 7 && score <= 8:
        return "passive";
      case score >= 9:
        return "promoter";
      default:
        throw new Error("unexpected score");
    }
  }, [score]);

  useEffect(() => {
    //
    logNpsSurveyEvent({ eventName: `comment_page.${copyType}.viewed` });
  }, [copyType]);

  const commentQuestion = useMemo(() => {
    switch (copyType) {
      case "detractor":
        return autoTranslate(
          "We’re sorry you don’t love your ClassDojo Plus subscription! Why wouldn’t you recommend it?",
        );
      case "passive":
        return autoTranslate("Thanks for rating your ClassDojo Plus subscription! How can we improve your experience?");
      case "promoter":
        return autoTranslate(
          "We’re so happy you're enjoying your ClassDojo Plus subscription! Why would you recommend it?",
        );
      default:
        return "";
    }
  }, [copyType]);
  const submit = useCallback(() => submitSurvey(copyType), [copyType, submitSurvey]);

  const innerHeightNumber = Number(innerHeight);
  return (
    <FullBleedScroller
      background="dt_background_primary"
      fixedContent={
        <div sx={{ position: "absolute", top: 12, left: 12 }}>
          <RoundedIconButton
            backgroundColor="dt_background_primary"
            onClick={closeSurvey}
            data-name="comment_page:close_button"
          >
            <IdeasCloseIcon color="dt_content_tertiary" />
          </RoundedIconButton>
        </div>
      }
    >
      <SurveyHeader innerHeight={innerHeightNumber} />
      <div sx={{ display: "flex", flexDirection: "column", position: "relative" }}>
        <SurveyQuestion question={commentQuestion} innerHeight={innerHeightNumber} />
        <CommentSection comment={comment} updateComment={updateComment} innerHeight={innerHeightNumber} />
      </div>
      <div sx={{ position: "relative", padding: RAW_cssValue(10) }}>
        <FullWidthButton
          data-name="comment_page:submit"
          text={autoTranslate("Submit")}
          disabled={comment === ""}
          onClick={submit}
          maxWidth={500}
        />
      </div>
    </FullBleedScroller>
  );
}
